import apiClient from './apiClient';

// ============================================================
// Types — matching backend Go models (FC130, FC131, FC132)
// ============================================================

export interface FC130 {
  fc130_01: string;       // 退回單號 (PK)
  fc130_10: string;       // 交運單號
  fc130_11: string | null; // 退回日期 (ISO string)
  fc130_12: string;       // 入庫庫位
  fc130_13: number;       // 總退回數量
  fc130_14: string;       // 旗標
  fc130_15: number;       // 未稅銷退淨額
  fc130_16: number;       // 稅額
  fc130_17: number;       // 預設值1
  fc130_18: string;       // 幣別
  fc130_19: number;       // 總項數
  fc130_20: number;       // 單據類別 (1=FC, 2=FJ)
  fc130_21: string | null; // 建立日期
  fc130_22: string;       // 客訴單號
  fc130_80: string;       // 核准狀態
  fc130_87: string;       // 核准人員
  fc130_88: string | null; // 核准日期
  fc130_89: string;       // 核准備註
  fc130_90: number;       // Version
  fc130_91: string;       // 輸單人員
  fc130_92: string | null; // 修改時間
  fc130_93: string;       // 建立人員
  fc130_94: string;       // 備用欄位

  // Associations
  details?: FC131[];
  board_details?: FC132[];
}

export interface FC131 {
  fc131_01: string;       // 退回單號 (FK)
  fc131_02: number;       // 行項次 (PK part)
  fc131_11: string;       // 產品代號(主碼)
  fc131_12: string;       // 產品代號(副碼)
  fc131_13: number;       // S/C項次
  fc131_14: string;       // 色號
  fc131_15: number;       // 未稅單價
  fc131_151: number;      // 含稅單價
  fc131_16: number;       // 退回數量
  fc131_17: number;       // 批號單位換算率
  fc131_18: string;       // 中文色名
  fc131_19: string;       // 英文色名
  fc131_20: string;       // 庫存單位
  fc131_21: number;       // 備用數值
  fc131_22: number;       // 交運批號項次
  fc131_23: string;       // 交運批號
  fc131_24: number;       // 交運單項次
  fc131_25: number;       // S/C顏色項次
  fc131_26: string;       // 庫存單位(F113)
  fc131_27: number;       // 庫存數量
  fc131_271: number;      // 含稅庫存退回數量
  fc131_28: number;       // 交運數量(原始)
  fc131_281: number;      // 含稅庫存交運數量
  fc131_29: string;       // 備註/附加碼
  fc131_30: boolean;      // 旗標
  fc131_31: string;       // 備用欄位
  fc131_90: number;       // Version
  fc131_91: string;       // 修改人員
  fc131_92: string | null; // 修改時間
  fc131_93: string;       // 建立人員
}

export interface FC132 {
  fc132_01: string;       // 退回單號 (FK)
  fc132_02: number;       // 明細項次
  fc132_03: number;       // 板號項次
  fc132_11: string;       // 板號
  fc132_90: number;       // Version
}

// ============================================================
// Request / Response types
// ============================================================

export interface C205ListResponse {
  data: FC130[];
  total: number;
  page: number;
  size: number;
}

export interface C205CreateRequest {
  master: Partial<FC130>;
  details: Partial<FC131>[];
  board_details: Partial<FC132>[];
}

export interface ValidationError {
  field: string;
  rule: string;
  message: string;
}

// ============================================================
// API methods
// ============================================================

export const c205Api = {
  async list(page = 1, size = 20): Promise<C205ListResponse> {
    const res = await apiClient.get('/api/c205', { params: { page, size } });
    return res.data;
  },

  async get(id: string): Promise<FC130> {
    const res = await apiClient.get(`/api/c205/${encodeURIComponent(id)}`);
    return res.data;
  },

  async importSource(sourceType: 'delivery' | 'sample', sourceNo: string): Promise<FC130> {
    const res = await apiClient.get(`/api/c205/source-import/${sourceType}/${encodeURIComponent(sourceNo)}`);
    return res.data;
  },

  async create(data: C205CreateRequest): Promise<FC130> {
    const res = await apiClient.post('/api/c205', data);
    return res.data;
  },

  async update(id: string, data: C205CreateRequest): Promise<FC130> {
    const res = await apiClient.put(`/api/c205/${encodeURIComponent(id)}`, data);
    return res.data;
  },

  async delete(id: string): Promise<void> {
    await apiClient.delete(`/api/c205/${encodeURIComponent(id)}`);
  },

  // Approval workflow
  async submitForApproval(id: string, comment?: string): Promise<{ message: string }> {
    const res = await apiClient.post(`/api/c205/${encodeURIComponent(id)}/submit`, { comment: comment || '' });
    return res.data;
  },

  async approve(id: string, comment?: string): Promise<{ message: string }> {
    const res = await apiClient.post(`/api/c205/${encodeURIComponent(id)}/approve`, { comment: comment || '' });
    return res.data;
  },

  async reject(id: string, comment?: string): Promise<{ message: string }> {
    const res = await apiClient.post(`/api/c205/${encodeURIComponent(id)}/reject`, { comment: comment || '' });
    return res.data;
  },

  async writeoff(id: string, comment?: string): Promise<{ message: string }> {
    const res = await apiClient.post(`/api/c205/${encodeURIComponent(id)}/writeoff`, { comment: comment || '' });
    return res.data;
  },

  async getApprovalHistory(id: string): Promise<unknown[]> {
    const res = await apiClient.get(`/api/c205/${encodeURIComponent(id)}/approval-history`);
    return res.data;
  },
};
